import { AppointmentStatus } from '$api-client/types.gen'
import { Text, Dropdown, DropdownOption, Tooltip } from '$components'
import { ChevronDownIcon } from '$components'
import { cn } from '$components'
import { useMemo, useState, useEffect } from 'react'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { Role } from '$api-client/types.gen'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { AppointmentStatusPillStyling, appointmentStatusPillStyling } from './styling'

export type StatusProps = {
  status: AppointmentStatus
  onStatusChange?: (status: AppointmentStatus) => void
} & AppointmentStatusPillStyling

const getStatusIcon = (status: AppointmentStatus) => {
  const StatusIcon = () => (
    <div className={cn('size-2 rounded-pill', {
      'bg-warning': status === AppointmentStatus.PENDING,
      'bg-danger': status === AppointmentStatus.CANCELLED || status === AppointmentStatus.NO_SHOW,
      'bg-info': status === AppointmentStatus.CONFIRMED,
      'bg-success': status === AppointmentStatus.COMPLETED,
    })}
    />
  )
  return StatusIcon
}

export function AppointmentStatusPill({ status, onStatusChange, size }: StatusProps) {
  const [optimisticStatus, setOptimisticStatus] = useState(status)
  const { user } = useAuth()
  const { hasSubscriptionExpired } = useTenant()

  // Sync internal state with prop changes (e.g., after query invalidation)
  useEffect(() => {
    setOptimisticStatus(status)
  }, [status])

  // Internal permission and subscription checks
  const hasPermission = PermissionsHelper.hasRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
  const isDisabled = hasSubscriptionExpired || !hasPermission
  const isInteractive = Boolean(onStatusChange)

  // Determine tooltip message
  const getTooltipMessage = () => {
    if (!onStatusChange) {return undefined}
    if (hasSubscriptionExpired) {return SUBSCRIPTION_EXPIRED_MESSAGE}
    if (!hasPermission) {return 'You are not allowed to update appointment status.'}
    return undefined
  }

  const tooltipMessage = getTooltipMessage()

  const handleStatusChange = (newStatus: AppointmentStatus) => {
    if (newStatus === optimisticStatus) { return }

    setOptimisticStatus(newStatus)
    onStatusChange?.(newStatus)
  }

  const statusOptions: DropdownOption[] = useMemo(() => [
    {
      id: AppointmentStatus.PENDING,
      value: AppointmentStatus.PENDING,
      icon: getStatusIcon(AppointmentStatus.PENDING),
      onClick: () => handleStatusChange(AppointmentStatus.PENDING),
    },
    {
      id: AppointmentStatus.NO_SHOW,
      value: AppointmentStatus.NO_SHOW,
      icon: getStatusIcon(AppointmentStatus.NO_SHOW),
      onClick: () => handleStatusChange(AppointmentStatus.NO_SHOW),
    },
    {
      id: AppointmentStatus.CANCELLED,
      value: AppointmentStatus.CANCELLED,
      icon: getStatusIcon(AppointmentStatus.CANCELLED),
      onClick: () => handleStatusChange(AppointmentStatus.CANCELLED),
    },
    {
      id: AppointmentStatus.CONFIRMED,
      value: AppointmentStatus.CONFIRMED,
      icon: getStatusIcon(AppointmentStatus.CONFIRMED),
      onClick: () => handleStatusChange(AppointmentStatus.CONFIRMED),
    },
    {
      id: AppointmentStatus.COMPLETED,
      value: AppointmentStatus.COMPLETED,
      icon: getStatusIcon(AppointmentStatus.COMPLETED),
      onClick: () => handleStatusChange(AppointmentStatus.COMPLETED),
    },
  ], [handleStatusChange])

  const pillContent = (
    <div className={cn(
      appointmentStatusPillStyling({ size }),
      {
        'cursor-pointer hover:border-border-strong transition-colors': isInteractive,
        'opacity-50 cursor-not-allowed': isDisabled && onStatusChange,
      },
    )}
    >
      {getStatusIcon(optimisticStatus)()}

      <Text type="s">
        {optimisticStatus}
      </Text>

      {isInteractive && (
        <ChevronDownIcon className="size-3 text-fg-subtle" />
      )}
    </div>
  )

  // If there's a callback but user is disabled or doesn't have permission show a tooltip
  if (onStatusChange && isDisabled) {
    return (
      <Tooltip content={tooltipMessage}>
        {pillContent}
      </Tooltip>
    )
  }

  // If interactive and user has permission, show dropdown
  if (isInteractive) {
    return (
      <Dropdown options={statusOptions}>
        {pillContent}
      </Dropdown>
    )
  }

  // Default: just show the pill
  return pillContent
}
